import classNames from "classnames";
import { useState } from "react";
import { ClickableIcon } from "../Icon/ClickableIcon.tsx";
import { Spinner } from "../Spinner/Spinner.tsx";
import { TextField } from "../TextField/TextField.tsx";

export const EditableText = ({
  value,
  placeholder = "",
  isLoading = false,
  className = "",
  textClassName = "",
  fieldClassName = "",
  onSave,
  onCancel,
  isEditable = true,
  allowEditOnClick = true,
}: {
  value: string;
  placeholder?: string;
  isLoading?: boolean;
  className?: string;
  textClassName?: string;
  fieldClassName?: string;
  onSave: (newValue: string) => void;
  onCancel?: () => void;
  isEditable?: boolean;
  allowEditOnClick?: boolean;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);

  const handleSave = () => {
    if (editValue.trim() && editValue !== value) {
      onSave(editValue);
    } else {
      handleCancel();
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditValue(value);
    onCancel?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSave();
      setIsEditing(false);
    } else if (e.key === "Escape") {
      handleCancel();
    }
  };

  const handleStartEditing = () => {
    setIsEditing(true);
    setEditValue(value);
  };

  if (isLoading) {
    return (
      <div className={className}>
        <Spinner size="sm" />
      </div>
    );
  }

  if (isEditable && isEditing) {
    return (
      <div className={classNames("w-full", className)}>
        <TextField
          size="sm"
          value={editValue}
          className={fieldClassName}
          onChange={(newValue) => setEditValue(newValue)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          autoFocus
        />
      </div>
    );
  }

  return (
    <div
      className={classNames("flex-row items-center gap-100 group", className)}
    >
      <div
        className={classNames(
          "truncate flex-fill",
          textClassName,
          isEditable && allowEditOnClick && "cursor-pointer",
        )}
        onClick={allowEditOnClick ? handleStartEditing : undefined}
      >
        {value || placeholder}
      </div>
      {isEditable && (
        <ClickableIcon
          name="PencilLine"
          size="sm"
          variant="tertiary"
          className="invisible group-hover:visible"
          onClick={handleStartEditing}
        />
      )}
    </div>
  );
};
